import React, { FC } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Group from '@mui/icons-material/Group';
import { Tooltip, IconButton, Switch } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useHistory } from 'react-router-dom';
import { PageUrl } from '../../../types/enums';
import Swal from 'sweetalert2';
import { OperatorCommand } from '../../../services/command';
import { OperatorQuery } from '../../../services/query';
import { useTranslation } from 'react-i18next';
import './SCUIRolesMainRow.scss';

export interface ISCUIRolesMainRowProps {
    row: any;
    refresh?: any;
    onRoleEdit?: (role: any) => void;
    onRoleUpdate?: (role: any) => void;
}
export const SCUIRolesMainRow: FC<ISCUIRolesMainRowProps> = ({ row, refresh, onRoleEdit, onRoleUpdate }) => {
    let history = useHistory();
    const onClickRole = (e: any) => {
        const roleID = e.currentTarget.dataset.roleid;
        const roleName = e.currentTarget.dataset.rolename;
        history.push(PageUrl.editUser.url + '/?RoleId=' + roleID, {
            roleId: roleID,
            roleName: roleName,
        });
    };
    const onClickAuthorizate = (e: any) => {
        const roleID = e.currentTarget.dataset.roleid;
        const roleName = e.currentTarget.dataset.rolename;
        history.push(PageUrl.authorizationManage.url + '/?RoleId=' + roleID, {
            roleId: roleID,
            roleName: roleName,
        });
    };
    const { t } = useTranslation();

    // Helper function to show error when role has operators
    const showRoleHasOperatorsError = (operatorCount: number) => {
        Swal.fire(
            t('general.couldntDeleted'),
            t('authorizationManagement.couldntdeletereason') + operatorCount,
            'error'
        );
    };

    // Helper function to handle successful deletion
    const handleSuccessfulDeletion = () => {
        Swal.fire(t('general.deleted'), ' ', 'success');
        refresh();
    };

    // Helper function to handle deletion error
    const handleDeletionError = (error?: any) => {
        Swal.fire(t('general.couldntDeleted'), ' ', 'error');
        if (error) {
            console.log('Hata :>> ', error);
        }
    };

    // Helper function to perform the actual deletion
    const performRoleDeletion = (roleId: any) => {
        OperatorCommand.DeleteRole(roleId)
            .then((res: any) => {
                if (res.statuscode == 200) {
                    handleSuccessfulDeletion();
                } else {
                    handleDeletionError();
                }
            })
            .catch((error: any) => {
                handleDeletionError(error);
            });
    };

    // Helper function to show confirmation dialog
    const showDeleteConfirmation = (row: any) => {
        Swal.fire({
            text: t('general.sureText') + ' ' + row.roleNameKey,
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.delete'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                performRoleDeletion(row.roleId);
            }
        });
    };

    function deleteRole(row: any) {
        OperatorQuery.GetOperatorListByRoleId(row.roleId)
            .then((res: any) => {
                if (res.model.data.length > 0) {
                    showRoleHasOperatorsError(res.model.data.length);
                } else {
                    showDeleteConfirmation(row);
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            });
    }

    return (
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }} key={row.rolesItemId}>
            <TableCell align="center">{row.roleNameKey}</TableCell>
            <TableCell align="center" sx={{ maxWidth: 200 }}>
                {row.description ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '150px',
                            }}
                        >
                            {row.description}
                        </div>
                        {row.description.length > 50 && (
                            <Tooltip title={row.description} arrow placement="top">
                                <IconButton size="small" sx={{ ml: 0.5 }}>
                                    <InfoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                ) : (
                    '-'
                )}
            </TableCell>
            <TableCell align="center">
                <Switch checked={row.isRequestable || false} disabled color="primary" />
            </TableCell>
            <TableCell align="center">
                <Button
                    sx={{ minWidth: '50px !important', height: '50px', borderRadius: '100%' }}
                    onClick={() => onRoleUpdate && onRoleUpdate(row)}
                    disabled={!onRoleUpdate}
                    title={t('authorizationManagement.editRole')}
                >
                    <EditIcon />
                </Button>
            </TableCell>
            <TableCell align="center">
                <Button
                    sx={{ minWidth: '50px !important', height: '50px', borderRadius: '100%' }}
                    onClick={() => onRoleEdit && onRoleEdit(row)}
                    disabled={!onRoleEdit}
                    title={t('roleManagement.assignRoleManager')}
                >
                    <AdminPanelSettingsIcon sx={{ width: '20px', height: '20px' }} />
                </Button>
            </TableCell>
            <TableCell align="center">
                <Button
                    sx={{ minWidth: '50px !important', height: '50px', borderRadius: '100%' }}
                    onClick={onClickRole}
                    data-roleid={row.roleId}
                    data-rolename={row.roleNameKey}
                >
                    <Group />
                </Button>
            </TableCell>
            <TableCell align="center">
                <Button
                    sx={{ minWidth: '50px !important', height: '50px', borderRadius: '100%' }}
                    onClick={onClickAuthorizate}
                    data-roleid={row.roleId}
                    data-rolename={row.roleNameKey}
                >
                    <SecurityIcon />
                </Button>
            </TableCell>
            <TableCell align="center">
                <Button
                    sx={{ minWidth: '50px !important', height: '50px', borderRadius: '100%' }}
                    onClick={() => deleteRole(row)}
                >
                    <DeleteIcon />
                </Button>
            </TableCell>
        </TableRow>
    );
};
